import {
  Dialog,
  DialogTitle,
  DialogContent,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  Autocomplete,
  Box,
  Typography,
} from "@mui/material";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { BASE_URL } from "../../constants";

const BillAdjustment = ({
  open,
  onClose,
  creditAmount = 0,
  existingData = [],
  onApply,
}) => {
  const rowRefs = useRef([]);
  const fieldOrder = ["particular", "paidAmount"];
  const [invoiceOptions, setInvoiceOptions] = useState([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [autocompleteOpen, setAutocompleteOpen] = useState(false);

  const [rows, setRows] = useState([
    { invoice: null, previousDue: 0, paidAmount: "", newDue: "" },
  ]);

  const handleArrowNavigation = (e, rowIndex, field) => {
    const colIndex = fieldOrder.indexOf(field);

    if (e.key === "ArrowRight") {
      e.preventDefault();
      if (colIndex < fieldOrder.length - 1) {
        focusCell(rowIndex, fieldOrder[colIndex + 1]);
      }
    }

    if (e.key === "ArrowLeft") {
      e.preventDefault();
      if (colIndex > 0) {
        focusCell(rowIndex, fieldOrder[colIndex - 1]);
      }
    }

    if (e.key === "ArrowDown") {
      if (autocompleteOpen) return;
      e.preventDefault();
      if (rowIndex < rows.length - 1) {
        focusCell(rowIndex + 1, field);
      }
    }

    if (e.key === "ArrowUp") {
      if (autocompleteOpen) return;
      e.preventDefault();
      if (rowIndex > 0) {
        focusCell(rowIndex - 1, field);
      }
    }
  };

  const focusCell = (rowIndex, field) => {
    requestAnimationFrame(() => {
      setTimeout(() => {
        rowRefs.current[rowIndex]?.[field]?.focus();
      }, 0);
    });
  };

  const totalPaid = rows.reduce((sum, r) => sum + Number(r.paidAmount || 0), 0);

  useEffect(() => {
    if (open) {
      // 🔥 IF EXISTING DATA → LOAD IT
      if (existingData.length > 0) {
        const mapped = existingData.map((item) => ({
          invoice: {
            _id: item._id,
            invoiceId: item.invoiceId || "NEW", // 🔥 manual case
            // patientName: a.particular || "",
            patient: item.particular, // 🔥 FIXED FIELD
          },
          isManual: item.isManual || false, // 🔥 IMPORTANT
          previousDue: item.previousDue || 0,
          paidAmount: item.paidAmount || "",
          newDue: item.newDue || 0,
        }));

        setRows(mapped);

        setTimeout(() => {
          focusCell(0, "paidAmount");
        }, 200);
      } else {
        // 🔥 FRESH ENTRY
        setRows([
          {
            invoice: null,
            previousDue: 0,
            paidAmount: "",
            newDue: "",
          },
        ]);

        setTimeout(() => {
          focusCell(0, "particular");
        }, 300);
      }
    }
  }, [open]);

  const handleEnterNavigation = (e, rowIndex, field) => {
    if (e.key !== "Enter") return;

    e.preventDefault();

    const colIndex = fieldOrder.indexOf(field);

    // 🔥 SHIFT + ENTER → LEFT
    if (e.shiftKey) {
      if (colIndex > 0) {
        focusCell(rowIndex, fieldOrder[colIndex - 1]);
      }
      return;
    }

    // 🔥 NORMAL ENTER → RIGHT
    if (colIndex < fieldOrder.length - 1) {
      focusCell(rowIndex, fieldOrder[colIndex + 1]);
      return;
    }

    // 🔥 LAST COLUMN → NEXT ROW
    if (rowIndex < rows.length - 1) {
      focusCell(rowIndex + 1, fieldOrder[0]);
    } else {
      // 👉 add new row if needed
      if (totalPaid < creditAmount) {
        setRows((prev) => [
          ...prev,
          {
            invoice: null,
            previousDue: 0,
            paidAmount: "",
            newDue: "",
          },
        ]);

        setTimeout(() => {
          focusCell(rowIndex + 1, fieldOrder[0]);
        }, 150);
      } else {
        // 🔥 FINAL VALIDATION
        if (Number(totalPaid) !== Number(creditAmount)) {
          toast.error("Total allocation must match credit amount");
          focusCell(rowIndex, field);
          return;
        }

        const allocationData = rows
          .filter((r) => r.invoice && r.paidAmount)
          .map((r) => ({
            _id: r.isManual ? null : r.invoice._id,
            invoiceId: r.isManual ? null : r.invoice.invoiceId,
            particular: r.invoice.patient,
            isManual: r.isManual || false,
            previousDue: r.previousDue,
            paidAmount: Number(r.paidAmount),
            newDue: r.newDue,
          }));

        onApply?.(allocationData);
        onClose();
      }
    }
  };

  const debounceRef = useRef(null);

  const fetchInvoices = async (search) => {
    if (!search || search.trim().length === 0) {
      setInvoiceOptions([]);
      return;
    }

    try {
      setLoadingInvoices(true);

      const res = await fetch(
        `${BASE_URL}api/user/common/search-invoices-due?query=${search}&limit=10`,
        {
          headers: {
          },
        },
      );

      const data = await res.json();

      if (data.success) {
        const mapped = data.invoices.map((inv) => ({
          _id: inv._id,
          invoiceId: inv.invoiceId,
          patient: inv.patientName,
          due: inv.due,
        }));

        setInvoiceOptions(mapped);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingInvoices(false);
    }
  };

  const handleSearch = (value) => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      fetchInvoices(value);
    }, 300); // 🔥 debounce time
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xl"
      PaperProps={{
        sx: {
          height: "92vh",
          mt: 1,
          borderRadius: 3,
          boxShadow: "0 10px 40px rgba(0,0,0,0.15)",
          overflow: "hidden",
        },
      }}
    >
      {/* HEADER */}
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          background: "linear-gradient(135deg, #1e293b, #334155)",
          color: "#fff",
          fontWeight: 600,
          fontSize: 18,
          px: 3,
          py: 2,
        }}
      >
        <ReceiptLongIcon sx={{ fontSize: 22 }} />
        Bill Adjustment
      </DialogTitle>

      <DialogContent sx={{ background: "#f8fafc" }}>
        <Box sx={{ maxHeight: "75vh", overflowY: "auto", p: 1 }}>
          <Table size="small" sx={{ tableLayout: "fixed" }}>
            {/* HEADER */}
            <TableHead>
              <TableRow
                sx={{
                  background: "#1e293b",
                  position: "sticky",
                  top: 0,
                  zIndex: 1,
                }}
              >
                {[
                  "Inv No",
                  "Particulars",
                  "Previous Due",
                  "Paid",
                  "New Due",
                ].map((h) => (
                  <TableCell
                    key={h}
                    sx={{
                      color: "#e2e8f0",
                      fontWeight: 600,
                      fontSize: 13,
                      width: h === "Particulars" ? "40%" : "15%",
                    }}
                  >
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            {/* BODY */}
            <TableBody>
              {rows.map((row, index) => (
                <TableRow
                  key={index}
                  hover
                  sx={{
                    "&:hover": { background: "#f1f5f9" },
                  }}
                >
                  {/* INV NO */}
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {/* INV NO */}
                      <TextField
                        size="small"
                        value={row.invoice?.invoiceId || ""}
                        disabled
                        fullWidth
                        sx={{
                          "& .MuiInputBase-root": {
                            height: 36,
                            background: "#e2e8f0",
                          },
                        }}
                      />
                    </Box>
                  </TableCell>

                  {/* PARTICULAR */}
                  <TableCell>
                    <Autocomplete
                      fullWidth
                      autoHighlight
                      freeSolo
                      size="small"
                      options={invoiceOptions}
                      loading={loadingInvoices}
                      onOpen={() => setAutocompleteOpen(true)}
                      onClose={() => setAutocompleteOpen(false)}
                      onInputChange={(e, value) => {
                        handleSearch(value);
                      }}
                      getOptionLabel={(opt) => {
                        if (typeof opt === "string") return opt;
                        if (!opt) return "";
                        return opt.patient;
                      }}
                      filterOptions={(options, state) => {
                        const input = state.inputValue.toLowerCase();
                        return options.filter(
                          (opt) =>
                            opt._id.toLowerCase().includes(input) ||
                            opt.patient.toLowerCase().includes(input),
                        );
                      }}
                      value={row.invoice}
                      onChange={(e, value) => {
                        const updated = [...rows];

                        // 🔥 CASE 1: SELECTED FROM LIST
                        if (value && typeof value === "object") {
                          updated[index].invoice = value;
                          updated[index].isManual = false;
                          updated[index].previousDue = value.due;
                          updated[index].paidAmount = "";
                          updated[index].newDue = value.due;
                        }

                        // 🔥 CASE 2: MANUAL ENTRY
                        else if (typeof value === "string") {
                          updated[index].invoice = {
                            _id: null,
                            invoiceId: "NEW",
                            patient: value,
                          };
                          updated[index].isManual = true;
                          updated[index].previousDue = 0;
                          updated[index].paidAmount = "";
                          updated[index].newDue = 0;
                        }

                        setRows(updated);

                        // 👉 move to Paid Amount
                        focusCell(index, "paidAmount");
                      }}
                      renderOption={(props, option) => (
                        <Box
                          component="li"
                          {...props}
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            width: "100%",
                          }}
                        >
                          <Typography>
                            {option.patient} ({option.invoiceId}){" "}
                          </Typography>
                          <Typography
                            sx={{
                              color: "#dc2626",
                              fontWeight: 600,
                              fontFamily: "monospace",
                            }}
                          >
                            ₹{option.due}
                          </Typography>
                        </Box>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          onKeyDown={(e) => {
                            handleArrowNavigation(e, index, "particular");
                            handleEnterNavigation(e, index, "particular");
                          }}
                          inputRef={(el) => {
                            if (!rowRefs.current[index])
                              rowRefs.current[index] = {};
                            rowRefs.current[index].particular = el; // 🔥 IMPORTANT
                          }}
                          placeholder="Search invoice / patient"
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {/* 🔥 NEW REF TAG */}
                                {row.isManual && (
                                  <Typography
                                    sx={{
                                      fontSize: 10,
                                      px: 0.8,
                                      py: 0.2,
                                      borderRadius: 1,
                                      background: "#fef3c7",
                                      color: "#92400e",
                                      fontWeight: 600,
                                      mr: 1,
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    NEW REF
                                  </Typography>
                                )}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                    />
                  </TableCell>

                  {/* PREVIOUS DUE */}
                  <TableCell>
                    <Typography
                      sx={{
                        color: row.previousDue > 0 ? "#dc2626" : "#16a34a",
                        fontWeight: 600,
                        fontFamily: "monospace",
                      }}
                    >
                      {row.previousDue ? `₹ ${row.previousDue}` : ""}
                    </Typography>
                  </TableCell>

                  {/* PAID */}
                  <TableCell>
                    <TextField
                      size="small"
                      fullWidth
                      value={row.paidAmount}
                      inputRef={(el) => {
                        if (!rowRefs.current[index])
                          rowRefs.current[index] = {};
                        rowRefs.current[index].paidAmount = el;
                      }}
                      inputProps={{ style: { textAlign: "right" } }}
                      sx={{
                        "& .MuiInputBase-root": {
                          height: 36,
                          background: "#ecfdf5",
                          fontWeight: 600,
                        },
                      }}
                      onChange={(e) => {
                        let val = Number(e.target.value || 0);
                        const prev = row.previousDue;

                        // 🔥 NORMAL INVOICE → restrict
                        if (!row.isManual && val > prev) {
                          val = prev;
                        }

                        const updated = [...rows];
                        updated[index].paidAmount = val;
                        const newDue = prev - val;

                        // 🔥 manual → always 0 minimum
                        updated[index].newDue = newDue < 0 ? 0 : newDue;

                        setRows(updated);
                      }}
                      onKeyDown={(e) => {
                        handleArrowNavigation(e, index, "paidAmount");

                        if (e.key === "Enter") {
                          e.preventDefault();

                          if (totalPaid < creditAmount) {
                            setRows((prev) => [
                              ...prev,
                              {
                                invoice: null,
                                previousDue: 0,
                                paidAmount: "",
                                newDue: "",
                              },
                            ]);

                            setTimeout(() => {
                              focusCell(index + 1, "particular");
                            }, 200);
                          } else {
                            // 🔥 VALIDATION
                            if (Number(totalPaid) !== Number(creditAmount)) {
                              toast.error(
                                "Total allocation must match credit amount",
                              );

                              // 👉 keep focus on current field
                              focusCell(index, "paidAmount");
                              return;
                            }

                            // ✅ PREPARE BILL DATA
                            const allocationData = rows
                              .filter((r) => r.invoice && r.paidAmount)
                              .map((r) => ({
                                _id: r.isManual ? null : r.invoice._id,
                                invoiceId: r.isManual
                                  ? null
                                  : r.invoice.invoiceId,
                                particular: r.invoice.patient,
                                isManual: r.isManual || false,
                                previousDue: r.previousDue,
                                paidAmount: Number(r.paidAmount),
                                newDue: r.newDue,
                              }));

                            onApply?.(allocationData);
                            onClose();
                          }
                        }
                      }}
                    />
                  </TableCell>

                  {/* NEW DUE */}
                  <TableCell>
                    <TextField
                      size="small"
                      fullWidth
                      value={row.newDue}
                      inputProps={{ style: { textAlign: "right" } }}
                      sx={{
                        "& .MuiInputBase-root": {
                          height: 36,
                          background: "#f1f5f9",
                          fontWeight: 600,
                        },
                      }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {/* FOOTER */}
          <Box
            sx={{
              mt: 2,
              px: 2,
              py: 1.5,
              borderRadius: 2,
              background: "#ffffff",
              border: "1px solid #e2e8f0",
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
            }}
          >
            {/* TOTAL PAID */}
            <Box sx={{ mr: 5 }}>
              <Typography fontSize={12} color="#64748b">
                Total Paid
              </Typography>
              <Typography
                fontWeight={700}
                sx={{
                  color: "#16a34a",
                  fontSize: 16,
                  fontFamily: "monospace",
                }}
              >
                ₹ {totalPaid.toLocaleString()}
              </Typography>
            </Box>

            {/* REMAINING */}
            <Box textAlign="right">
              <Typography fontSize={12} color="#64748b">
                Remaining
              </Typography>
              <Typography
                fontWeight={700}
                sx={{
                  color: creditAmount - totalPaid === 0 ? "#16a34a" : "#dc2626",
                  fontSize: 16,
                  fontFamily: "monospace",
                }}
              >
                ₹ {(creditAmount - totalPaid).toLocaleString()}
              </Typography>
            </Box>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default BillAdjustment;
